export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your shoulder rehabilitation resources were already organized by surgical procedure?',
    paragraphs: [
      'Two patients can undergo shoulder surgery and require completely different approaches. A rotator cuff repair is not rehabilitated like a stabilization. Biceps tenodesis is not tenotomy. Anatomic and reverse replacements need different strategies.',
      'In clinical practice, you may encounter:',
    ],
    list: [
      'Multiple repaired structures with overlapping restrictions.',
      'Uncertainty about which exercises suit a specific procedure.',
      'Persistent weakness despite improved passive mobility.',
      'Scapular compensations during shoulder elevation.',
      'Doubts about when to introduce resistance or functional loading.',
      'Complex recoveries following revision surgery.',
    ],
    outro: [
      'Complete Postoperative Shoulder Rehabilitation System™ brings together seven specialized areas of postoperative shoulder rehabilitation, plus five collections of professional clinical tools.',
    ],
    highlight: 'Identify. Protect. Mobilize. Strengthen. Progress.',
  },
  {
    variant: 'gold' as const,
    title: 'Less repetitive preparation. More procedure-specific resources.',
    paragraphs: [
      "You shouldn't have to rebuild exercise sequences or search multiple documents for restrictions. Open the relevant volume, locate the procedure, review the exercises and progressions, and adapt them to your patient.",
    ],
    outro: [
      'Interventions include step-by-step instructions, positioning, dosage, modifications, precautions, and progression criteria.',
    ],
    highlight: 'Organized by surgical procedure — not generic exercises for every patient.',
  },
  {
    variant: 'navy' as const,
    title: 'Every surgical procedure has different needs. Your resources should reflect that.',
    paragraphs: [],
    qa: [
      { q: 'Rotator cuff repair?', a: 'Protected mobility, active control, and progressive strengthening based on the repaired structures.' },
      { q: 'Shoulder stabilization surgery?', a: 'Distinct interventions for anterior and posterior Bankart, Latarjet, and remplissage.' },
      { q: 'Biceps or superior labral procedures?', a: 'Movement restrictions, procedure-specific loading, and muscular control.' },
      { q: 'Shoulder replacement?', a: 'Anatomic vs. reverse arthroplasty, including deltoid and subscapularis considerations.' },
      { q: 'Surgically treated fractures?', a: 'Mobility and strengthening adapted to fixation and tuberosity involvement.' },
      { q: 'Advanced reconstruction?', a: 'Graft protection, transferred-muscle reeducation, and revision progressions.' },
    ],
    highlight: 'Open the volume. Find the intervention. Adapt it to your patient.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Small & Medium Rotator Cuff Repair',
    subtitle: 'Rehabilitation After Small and Medium Rotator Cuff Repairs',
    points: ['Procedure-specific resources for restoring mobility, muscular control, and strength.'],
    list: [
      'Protected mobility',
      'Passive and assisted elevation',
      'Rotator cuff activation',
      'Scapular control',
      'Progressive resistance',
      'Functional recovery',
    ],
    finalLine: 'Protect. Mobilize. Activate. Strengthen.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '02',
    title: 'Large, Massive & Subscapularis Repair',
    subtitle: 'Rehabilitation After Large, Massive, and Subscapularis Repairs',
    points: ['Protocols for repairs requiring specialized protection and loading, including combined tendon repairs.'],
    list: [
      'Mobility modifications',
      'Rotational restrictions',
      'Active-control exercises',
      'Structure-adapted strengthening',
    ],
    finalLine: 'Protect. Differentiate. Control. Progress.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '03',
    title: 'Shoulder Instability & Stabilization Surgery',
    subtitle: 'Rehabilitation After Shoulder Stabilization Procedures',
    points: ['Anterior Bankart, posterior Bankart, Latarjet, and Bankart repair with remplissage.'],
    list: [
      'Protected mobility',
      'Rotational restrictions',
      'Neuromuscular control',
      'Dynamic stability',
      'Progressive strengthening',
      'Procedure-specific considerations',
    ],
    finalLine: 'Stabilize. Control. Strengthen. Recover.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '04',
    title: 'SLAP Repair, Biceps Procedures & Capsular Release',
    subtitle: 'Rehabilitation After SLAP, Biceps, and Capsular Procedures',
    points: ['SLAP repair, suprapectoral and subpectoral tenodesis, biceps tenotomy, and arthroscopic capsular release.'],
    list: [
      'Authorized mobility',
      'Repair and fixation protection',
      'Progressive biceps loading',
      'Post-release movement restoration',
    ],
    finalLine: 'Identify. Mobilize. Dose. Strengthen.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '05',
    title: 'Anatomic & Reverse Shoulder Arthroplasty',
    subtitle: 'Rehabilitation After Anatomic and Reverse Shoulder Replacement',
    points: ['Interventions selected by arthroplasty type and the structures involved.'],
    list: [
      'Subscapularis considerations (anatomic)',
      'Deltoid activation (reverse)',
      'Functional progressions',
      'Prosthetic stability precautions',
    ],
    finalLine: 'Mobilize. Activate. Stabilize. Strengthen.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '06',
    title: 'Shoulder Fracture Fixation & AC Reconstruction',
    subtitle: 'Rehabilitation After Fracture Fixation and AC Reconstruction',
    points: ['Proximal humerus ORIF, clavicle fixation, and acromioclavicular/coracoclavicular reconstruction.'],
    list: [
      'Protected mobility',
      'Active-movement progression',
      'Scapular control',
      'Fixation-based loading changes',
    ],
    finalLine: 'Protect. Mobilize. Verify. Progress.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
  {
    number: '07',
    title: 'Advanced Shoulder Reconstruction',
    subtitle: 'Rehabilitation After Complex Reconstructive Procedures',
    points: [
      'Superior capsular reconstruction, lower trapezius, latissimus dorsi, and pectoralis major transfers, revision cuff repair, and graft augmentation.',
    ],
    list: [
      'Graft protection',
      'Transferred-muscle reeducation',
      'Movement control',
      'Functional progression',
    ],
    finalLine: 'Protect. Retrain. Control. Recover.',
    meta: '40 Pages of Advanced Rehabilitation Resources',
  },
];

// Pages shown in the "Take a look inside" carousel, in order 1–6.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/qZPgCT6Xjr1AsUQnFnBtit/Captura_de_pantalla_2026-10-25_202829.png',
    alt: 'Distal mobility without compromising shoulder protection — early distal intervention panel.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/vMaBH5VZGGnecQHgTpH5h5/Captura_de_pantalla_2026-10-25_202849.png',
    alt: 'Passive forward elevation — therapist-assisted passive elevation protocol.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/rearUCfKszztBwNW4vduqf/Captura_de_pantalla_2026-10-25_202913.png',
    alt: 'Supine assisted elevation — wand-assisted elevation program.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/kyeEKDNMRYKFn3UQxnXmP2/Captura_de_pantalla_2026-10-25_202935.png',
    alt: 'Active elevation — supine to incline gravity-modified progression.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/4YxvNEzSTHy7DEYwPngjDe/Captura_de_pantalla_2026-10-25_202952.png',
    alt: 'Scapular control during active arm movement — scapulohumeral motor control program.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/mH1TiJ36dW32ozkVCzz7e4/Captura_de_pantalla_2026-10-25_203011.png',
    alt: 'Side-lying external rotation — controlled isotonic loading progression.',
  },
];

export const insideCategories = [
  { title: 'Rotator Cuff Repair', text: 'Protected mobility, activation, exercises, and strength progressions.' },
  { title: 'Shoulder Stabilization', text: 'Procedure-specific restrictions, dynamic control, and strengthening.' },
  { title: 'Biceps & Capsular Procedures', text: 'Mobility, progressive loading, and functional recovery.' },
  { title: 'Shoulder Arthroplasty', text: 'Deltoid activation and mobility based on arthroplasty type.' },
  { title: 'Fractures & AC Reconstruction', text: 'Movement and loading adapted to bone and ligament fixation.' },
  { title: 'Advanced Reconstruction', text: 'Transferred-muscle reeducation and graft protection.' },
];

export const bonuses = [
  {
    number: '01',
    title: 'Surgical Decision & Restrictions Toolkit',
    subtitle: 'Advanced Tools for Interpreting Surgical Decisions and Restrictions',
    price: 12.9,
    intro: [
      'Translate surgical information into practical rehabilitation decisions: analyze operative reports, identify protected structures, integrate combined-procedure restrictions, and verify when a movement or load requires authorization.',
    ],
    finalLine: 'Identify. Verify. Integrate. Decide.',
    meta: '40 Pages of Professional Clinical Tools',
  },
  {
    number: '02',
    title: 'Combined-Surgery Clinical Casebook',
    subtitle: 'Advanced Clinical Cases Involving Combined Shoulder Procedures',
    price: 10.9,
    intro: [
      'Eight fully developed fictional cases where multiple repairs create overlapping restrictions — cuff repair with tenodesis, stabilization with remplissage, Latarjet with subscapularis procedures, arthroplasty with associated repairs, and more.',
    ],
    finalLine: 'Analyze. Compare. Solve. Adapt.',
    meta: '40 Pages of Advanced Clinical Cases',
  },
  {
    number: '03',
    title: 'Patient Home Rehabilitation System',
    subtitle: 'Home Programs for Patients After Shoulder Surgery',
    price: 9.9,
    intro: ['Ten home programs organized by procedure and authorized stage, including:'],
    list: [
      'Fully developed exercises',
      'Patient positioning',
      'Step-by-step instructions',
      'Customizable dosage',
      'Home modifications',
      'Specific precautions',
    ],
    finalLine: 'Select. Customize. Explain. Support.',
    meta: '40 Pages of Home Rehabilitation Programs',
  },
  {
    number: '04',
    title: 'Clinical Outcomes & Load Monitoring',
    subtitle: 'Tools for Assessing Recovery and Monitoring Loads',
    price: 12.9,
    intro: [
      'Track mobility, strength, symptoms, exercise tolerance, training volume, compensation, and function — plus resources for analyzing plateaus and organizing follow-up reports.',
    ],
    finalLine: 'Measure. Record. Compare. Adjust.',
    meta: '40 Pages of Clinical Monitoring Tools',
  },
  {
    number: '05',
    title: 'Advanced Return-to-Function Testing',
    subtitle: 'Advanced Assessments for Functional Recovery',
    price: 15.9,
    intro: ['Five testing domains, always respecting surgical clearance:'],
    list: [
      'Overhead sports',
      'Contact and collision sports',
      'Heavy and overhead work',
      'Return to resistance training',
      'Everyday independence',
    ],
    finalLine: 'Assess. Verify. Adapt. Progress.',
    meta: '40 Pages of Functional Assessment Resources',
  },
];

export const benefits = [
  { title: 'Advanced clinical content', text: 'Specific procedures and complex situations — not elementary explanations.' },
  { title: 'Fully developed exercises', text: 'Instructions, positioning, dosage, modifications, and progression criteria.' },
  { title: 'Procedure-specific rehabilitation', text: 'Cuff repairs, stabilization, arthroplasty, fractures, and reconstruction.' },
  { title: 'Mobility & strength progressions', text: 'From protected movement to functional recovery.' },
  { title: 'Clinical decision-making tools', text: 'Identify restrictions, adapt interventions, document decisions.' },
  { title: 'Patient rehabilitation programs', text: 'Home resources you can customize to each patient.' },
  { title: 'Assessment and follow-up', text: 'Record mobility, strength, loading response, and function.' },
  { title: 'One organized collection', text: 'Seven clinical volumes and five bonuses in one system.' },
];

export const offerItems = [
  '7 advanced clinical volumes',
  '5 FREE professional bonuses',
  'Protocols and exercises organized by surgical procedure',
  'Mobility, muscular control, and strengthening interventions',
  'Restriction tools, complex cases, and home programs',
  'Outcomes monitoring and return-to-function assessments',
];

export const audiences = [
  { title: 'Shoulder rehab physical therapists', text: 'Procedure-specific protocols, exercises, and progressions.' },
  { title: 'Sports physical therapists', text: 'Muscular control and progression toward authorized sports activity.' },
  { title: 'Orthopedic rehab specialists', text: 'Tools for complex repairs, arthroplasty, fixation, and reconstruction.' },
  { title: 'Hospital & outpatient PTs', text: 'Treatment planning, documentation, and restriction coordination.' },
  { title: 'Experienced postoperative clinicians', text: 'Practical tools that complement your independent clinical judgment.' },
];

export const testimonials = [
  {
    name: 'Rachel M.',
    age: 38,
    role: 'Shoulder rehabilitation physical therapist · Chicago, USA',
    quote:
      'Half my caseload is post-op shoulders, and no two operative reports look the same. Last month I had a cuff repair combined with a subpectoral tenodesis — the restrictions toolkit helped me map out what was protected before the first session. Volume 01 is now my starting point for every small and medium repair.',
  },
  {
    name: 'Daniel O.',
    age: 45,
    role: 'Orthopedic rehabilitation specialist · Leeds, UK',
    quote:
      "Reverse shoulder replacements are a big part of my practice. Finally having deltoid-focused progressions laid out separately from anatomic arthroplasty — with dosage and regressions already developed — has saved me hours of rebuilding handouts. I customize the home programs and my older patients actually follow them.",
  },
  {
    name: 'Valentina R.',
    age: 33,
    role: 'Sports physical therapist · Buenos Aires, Argentina',
    quote:
      'I work with overhead athletes after Latarjet and Bankart procedures. The stabilization volume differentiates each surgery clearly, and the return-to-function tests give me a structured way to document progress before talking to the surgeon about clearance.',
  },
];

export const faqs = [
  { q: 'Is this a physical product?', a: 'No. It is a 100% digital collection of downloadable PDF resources. Nothing is shipped.' },
  {
    q: 'Who is this system designed for?',
    a: 'Experienced physical therapists, musculoskeletal and sports rehabilitation specialists, and clinicians treating patients after shoulder surgery.',
  },
  {
    q: 'Is this a course or certification?',
    a: 'No. It is a collection of practical clinical resources. It does not provide certification or replace accredited education.',
  },
  {
    q: 'Is this material for beginners?',
    a: 'No. It is for professionals who already understand shoulder anatomy, surgical procedures, and rehabilitation principles.',
  },
  {
    q: 'Which surgical procedures are covered?',
    a: 'Rotator cuff and subscapularis repairs, stabilization, SLAP and biceps procedures, capsular release, anatomic and reverse arthroplasty, fracture fixation, AC reconstruction, and advanced reconstruction.',
  },
  {
    q: 'Do the exercises include sets and repetitions?',
    a: 'Yes — execution, positioning, dosage guidance, modifications, precautions, and progression criteria. Final prescription must be individualized.',
  },
  {
    q: 'Does it include resources I can give to patients?',
    a: 'Yes. Bonus 03 includes ten home rehabilitation programs you can customize before providing them to patients.',
  },
  {
    q: 'Does it include return-to-sport or return-to-work testing?',
    a: 'Yes. Bonus 05 covers sports, physical work, resistance training, and everyday independence. It does not replace medical clearance.',
  },
  {
    q: "Do these protocols replace the surgeon's instructions?",
    a: "No. The surgeon's instructions, operative report, and patient-specific restrictions always take priority.",
  },
  {
    q: 'How will I receive my purchase?',
    a: "You'll receive access instructions right after payment. Trouble accessing? Contact compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Emily Carter', place: 'Seattle, United States', minutes: 3 },
  { name: 'Jonas Becker', place: 'Hamburg, Germany', minutes: 7 },
  { name: 'Martina López', place: 'Bogotá, Colombia', minutes: 5 },
  { name: 'Liam Walsh', place: 'Cork, Ireland', minutes: 12 },
  { name: 'Giulia Rossi', place: 'Milan, Italy', minutes: 4 },
  { name: 'Priya Sharma', place: 'Vancouver, Canada', minutes: 9 },
  { name: 'Oliver Bennett', place: 'Melbourne, Australia', minutes: 14 },
  { name: 'Diego Fernández', place: 'Madrid, Spain', minutes: 6 },
  { name: 'Ana Souza', place: 'São Paulo, Brazil', minutes: 11 },
  { name: 'Kenji Watanabe', place: 'Osaka, Japan', minutes: 8 },
];
